import { CanvasAddon } from '@xterm/addon-canvas';
import { Unicode11Addon } from '@xterm/addon-unicode11';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { WebLinksAddon } from '@xterm/addon-web-links';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { WebglAddon } from '@xterm/addon-webgl';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Terminal } from '@xterm/xterm';
import FontFaceObserver from 'fontfaceobserver';
import React, { useEffect, useRef, useCallback } from 'react';
import '@xterm/xterm/css/xterm.css';

const isWebGl2Supported = !!document.createElement('canvas').getContext('webgl2');

async function waitForWebFontAsync(term: Terminal) {
  const fontFamily = term.options.fontFamily;
  if (!fontFamily) return;
  const regular = new FontFaceObserver(fontFamily).load();
  try {
    await regular;
  } catch {
    term.options.fontFamily = 'monospace';
  }
}

const useBind = (termRef: React.RefObject<Terminal>, handler: (...args: any[]) => void, eventName: 'onData') => {
  useEffect(() => {
    if (!termRef.current || typeof handler !== 'function') return;
    const term = termRef.current;
    const eventBinding = term[eventName](handler);
    return () => {
      if (!eventBinding) return;
      eventBinding.dispose();
    };
  }, [handler]);
};

interface XTermProps {
  onData: (data: string) => void;
  onInit: (term: Terminal) => void;
  onResize?: (cols: number, rows: number) => void;
  initialCols?: number;
  initialRows?: number;
  keepTerminalMounted?: boolean; // Don't dispose terminal on unmount
}

export const XTerm: React.FC<XTermProps> = ({
  onData,
  onInit,
  onResize,
  initialCols = 80,
  initialRows = 24,
  keepTerminalMounted = false,
}) => {
  const termRef = useRef<Terminal>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const onInitRef = useRef(onInit);
  const onResizeRef = useRef(onResize);
  const lastResizeRef = useRef({ cols: 80, rows: 24 });

  // Update refs when props change
  useEffect(() => {
    onInitRef.current = onInit;
  }, [onInit]);

  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  // Method to resize terminal - only this should be used
  const resizeTerminal = useCallback((cols: number, rows: number) => {
    if (termRef.current) {
      termRef.current.resize(cols, rows);
      lastResizeRef.current = { cols, rows };
    }
  }, []);

  // Method to clear terminal
  const clearTerminal = useCallback(() => {
    if (termRef.current) {
      termRef.current.clear();
    }
  }, []);

  // Method to restore buffer
  const restoreBuffer = useCallback((buffer: string) => {
    if (termRef.current && buffer) {
      termRef.current.write(buffer);
    }
  }, []);

  // Method to get current buffer
  const getBuffer = useCallback(() => {
    if (!termRef.current) return '';

    const buffer = termRef.current.buffer.active;
    let savedBuffer = '';

    for (let i = 0; i < buffer.length; i++) {
      const line = buffer.getLine(i);
      if (line) {
        savedBuffer += line.translateToString(true) + '\n';
      }
    }

    return savedBuffer;
  }, []);

  // Expose methods to parent
  useEffect(() => {
    if (termRef.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      termRef.current.resizeTerminal = resizeTerminal;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      termRef.current.clearTerminal = clearTerminal;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      termRef.current.restoreBuffer = restoreBuffer;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      termRef.current.getBuffer = getBuffer;
    }
  }, [resizeTerminal, clearTerminal, restoreBuffer, getBuffer]);

  React.useEffect(() => {
    const term = new Terminal({
      allowProposedApi: true,
      rows: initialRows, // Use provided initial dimensions
      cols: initialCols, // Use provided initial dimensions
      fontSize: 14,
      fontFamily: 'monospace',
      theme: {
        background: '#1a1a1a',
        foreground: '#ffffff',
        cursor: '#ffffff',
      },
      // Prevent automatic resizing
      allowTransparency: false,
      scrollback: 1000,
    });

    term.loadAddon(new Unicode11Addon());
    term.unicode.activeVersion = '11';
    term.loadAddon(new WebLinksAddon());

    if (isWebGl2Supported) {
      const webGl2Addon = new WebglAddon();
      webGl2Addon.onContextLoss(() => {
        webGl2Addon.dispose();
      });
      term.loadAddon(webGl2Addon);
    } else {
      term.loadAddon(new CanvasAddon());
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    termRef.current = term;

    waitForWebFontAsync(term).then(() => {
      if (termRef.current !== term) {
        return;
      }
      if (!termRef.current) {
        return;
      }
      termRef.current.open(divRef.current!);
      // Don't call resizeTerminal here - let parent control the dimensions
      onInitRef.current(term);
    });

    return () => {
      // Only dispose if not keeping terminal mounted
      if (!keepTerminalMounted) {
        try {
          term.dispose();
        } catch (ex) {
          console.error('An error occurred during disposal of the XTermJS');
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        termRef.current = null;
      }
    };
  }, [keepTerminalMounted]); // Depend on keepTerminalMounted

  useBind(termRef, onData, 'onData');

  return <div id='terminal' ref={divRef} className='h-full w-full rounded-lg overflow-hidden pb-2'></div>;
};
